import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Collapse, Tabs, Button, Spin, Typography, message as antMessage } from 'antd';
import { DownOutlined, RightOutlined, DownloadOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * PaperCommunication Component
 * 
 * A document editor for letters, faxes, etc. that distinguishes between 
 * template content and AI-generated content.
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing template and generated values
 * @param {string} props.data.template_markdown - Template with <<placeholder>> syntax
 * @param {Object} props.data.template_values - Values for placeholders (from state or LLM-derived)
 * @param {string} props.title - The title of the document
 * @param {string} props.uuid - The UUID of the component
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {string} props.sessionId - The current session ID
 * @param {string} props.project_id - The project ID
 * @param {string} props.agent_key - The agent key
 * @param {string} props.apiUrl - The base API URL for the agent (e.g., http://localhost:8035)
 * @param {string} props.pdfEndpoint - The endpoint path for PDF generation (default: /generate_pdf)
 */
const PaperCommunication = ({
  data,
  title = "Communication",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  project_id,
  agent_key,
  apiUrl = '',
  pdfEndpoint = '/generate_pdf'
}) => {
  // State for component expansion
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState('edit');
  
  // State for editable values (initialized from data.generated_values)
  const [editedValues, setEditedValues] = useState({});
  
  // State for PDF generation
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  
  // Ref to track if content has changed since last PDF generation
  const contentHashRef = useRef(null);
  
  // Initialize edited values from data
  useEffect(() => {
    if (data?.template_values) {
      setEditedValues(data.template_values);
    }
  }, [data?.template_values]);
  
  // Cleanup PDF blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);
  
  // Register component for updates
  useEffect(() => {
    if (registerComponent && uuid) {
      const refreshComponent = () => {
        console.log(`Refreshing PaperCommunication: ${uuid}`);
      };
      const unregister = registerComponent(refreshComponent);
      return unregister;
    }
  }, [registerComponent, uuid]);
  
  /**
   * Generate a simple hash of the current content for change detection
   */
  const generateContentHash = useCallback(() => {
    const content = JSON.stringify({
      template: data?.template_markdown,
      values: editedValues
    });
    return content;
  }, [data?.template_markdown, editedValues]);
  
  /**
   * Merge template with values to create final document content
   */
  const getMergedContent = useCallback(() => {
    if (!data?.template_markdown) return '';
    
    let content = data.template_markdown;
    
    // Replace all placeholders with their values
    Object.entries(editedValues).forEach(([key, value]) => {
      const placeholder = `<<${key}>>`;
      content = content.split(placeholder).join(value || placeholder);
    });
    
    return content;
  }, [data?.template_markdown, editedValues]);
  
  /**
   * Handle value change for AI-generated content
   */
  const handleValueChange = (key, newValue) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: newValue
    }));
  };
  
  /**
   * Parse template and render with editable AI-generated sections
   */
  const renderEditableDocument = () => {
    if (!data?.template_markdown) {
      return <Text type="secondary">No template available.</Text>;
    }
    
    const template = data.template_markdown;
    
    // Find all placeholders in the template
    const placeholderRegex = /<<([^>]+)>>/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = placeholderRegex.exec(template)) !== null) {
      // Add text before the placeholder
      if (match.index > lastIndex) {
        const textBefore = template.substring(lastIndex, match.index);
        parts.push({
          type: 'template',
          content: textBefore,
          key: `template-${lastIndex}`
        });
      }
      
      // Add the placeholder (AI-generated content)
      const placeholderName = match[1];
      parts.push({
        type: 'generated',
        placeholderName: placeholderName,
        content: editedValues[placeholderName] || `<<${placeholderName}>>`,
        key: `generated-${placeholderName}-${match.index}`
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last placeholder
    if (lastIndex < template.length) {
      parts.push({
        type: 'template',
        content: template.substring(lastIndex),
        key: `template-${lastIndex}`
      });
    }
    
    return (
      <div style={styles.documentContainer}>
        {parts.map((part) => {
          if (part.type === 'template') {
            return renderTemplateText(part.content, part.key);
          } else {
            return renderEditableField(part.placeholderName, part.content, part.key);
          }
        })}
      </div>
    );
  };
  
  /**
   * Render template (static) text with proper line breaks
   */
  const renderTemplateText = (text, key) => {
    // Split by \n\n for paragraphs, then by \n for line breaks
    const paragraphs = text.split(/\\n\\n|\n\n/);
    
    return (
      <span key={key} style={styles.templateText}>
        {paragraphs.map((paragraph, pIndex) => {
          const lines = paragraph.split(/\\n|\n/);
          return (
            <span key={`${key}-p-${pIndex}`}>
              {pIndex > 0 && <><br /><br /></>}
              {lines.map((line, lIndex) => (
                <span key={`${key}-p-${pIndex}-l-${lIndex}`}>
                  {lIndex > 0 && <br />}
                  {line}
                </span>
              ))}
            </span>
          );
        })}
      </span>
    );
  };
  
  /**
   * Render editable field for AI-generated content
   */
  const renderEditableField = (placeholderName, content, key) => {
    const isPlaceholder = content === `<<${placeholderName}>>`;
    const isMultiline = content.includes('\\n') || content.includes('\n') || 
                        placeholderName === 'main_content';
    
    // For multiline content like main_content, use a textarea-like display
    if (isMultiline && !isPlaceholder) {
      return (
        <span
          key={key}
          style={styles.editableFieldWrapper}
          title={`AI Generated: ${placeholderName}`}
        >
          <span style={styles.aiIndicator}>AI</span>
          <span
            contentEditable
            suppressContentEditableWarning
            style={styles.editableFieldMultiline}
            onBlur={(e) => {
              const newValue = e.target.innerText;
              handleValueChange(placeholderName, newValue);
            }}
            dangerouslySetInnerHTML={{
              __html: formatContentForDisplay(content)
            }}
          />
        </span>
      );
    }
    
    // For single-line values or unfilled placeholders
    return (
      <span
        key={key}
        style={{
          ...styles.editableFieldWrapper,
          display: 'inline'
        }}
        title={`AI Generated: ${placeholderName}`}
      >
        <span style={styles.aiIndicatorInline}>AI</span>
        <span
          contentEditable
          suppressContentEditableWarning
          style={{
            ...styles.editableFieldInline,
            ...(isPlaceholder ? styles.unfilled : {})
          }}
          onBlur={(e) => {
            const newValue = e.target.innerText;
            handleValueChange(placeholderName, newValue);
          }}
        >
          {isPlaceholder ? `[${placeholderName}]` : content}
        </span>
      </span>
    );
  };
  
  /**
   * Format content for display, converting \n to <br>
   */
  const formatContentForDisplay = (content) => {
    if (!content) return '';
    return content
      .replace(/\\n\\n/g, '<br><br>')
      .replace(/\\n/g, '<br>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');
  };
  
  /**
   * Generate PDF via backend endpoint
   */
  const generatePdf = async () => {
    setPdfLoading(true);
    setPdfError(null);
    
    // Clean up previous blob URL
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    
    try {
      const mergedContent = getMergedContent();
      
      // Construct the full URL from apiUrl and pdfEndpoint
      // Remove trailing slash from apiUrl and leading slash from endpoint if present
      const baseUrl = apiUrl.replace(/\/$/, '');
      const endpoint = pdfEndpoint.startsWith('/') ? pdfEndpoint : `/${pdfEndpoint}`;
      const fullUrl = `${baseUrl}${endpoint}`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          content: mergedContent,
          document_type: 'letter',
          title: title,
          uuid: uuid,
          project_id: project_id,
          agent_key: agent_key
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      
      // Store content hash for change detection
      contentHashRef.current = generateContentHash();
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfError(error.message);
      antMessage.error(`Failed to generate PDF: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };
  
  /**
   * Handle tab change
   */
  const handleTabChange = (key) => {
    setActiveTab(key);
    
    // Generate PDF when switching to preview tab
    if (key === 'preview') {
      generatePdf();
    }
  };
  
  /**
   * Handle PDF download
   */
  const handleDownload = () => {
    if (!pdfBlobUrl) {
      antMessage.warning('Please wait for PDF to generate');
      return;
    }
    
    const link = document.createElement('a');
    link.href = pdfBlobUrl;
    link.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    antMessage.success('PDF downloaded successfully');
  };
  
  /**
   * Render PDF preview tab content
   */
  const renderPdfPreview = () => {
    if (pdfLoading) {
      return (
        <div style={styles.pdfLoadingContainer}>
          <Spin size="large" />
          <Text style={{ marginTop: '16px' }}>Generating PDF...</Text>
        </div>
      );
    }
    
    if (pdfError) {
      return (
        <div style={styles.pdfErrorContainer}>
          <Text type="danger">Error generating PDF: {pdfError}</Text>
          <Button 
            type="primary" 
            onClick={generatePdf}
            style={{ marginTop: '16px' }}
          >
            Retry
          </Button>
        </div>
      );
    }
    
    if (pdfBlobUrl) {
      return (
        <div style={styles.pdfPreviewContainer}>
          <iframe
            src={pdfBlobUrl}
            style={styles.pdfIframe}
            title="PDF Preview"
          />
        </div>
      );
    }
    
    return (
      <div style={styles.pdfLoadingContainer}>
        <Text type="secondary">Switch to this tab to generate PDF preview</Text>
      </div>
    );
  };
  
  // Tab items configuration
  const tabItems = [
    {
      key: 'edit',
      label: (
        <span>
          <EditOutlined />
          Edit
        </span>
      ),
      children: (
        <div style={styles.editTabContent}>
          {renderEditableDocument()}
        </div>
      )
    },
    {
      key: 'preview',
      label: (
        <span>
          <FileTextOutlined />
          PDF Preview
        </span>
      ),
      children: renderPdfPreview()
    }
  ];
  
  // Extra content for tabs (download button)
  const tabBarExtraContent = {
    right: (
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleDownload}
        disabled={!pdfBlobUrl || pdfLoading}
        style={{
          backgroundColor: '#6200EE',
          borderColor: '#6200EE'
        }}
      >
        Download PDF
      </Button>
    )
  };
  
  return (
    <div style={styles.container}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={styles.collapseHeader}>
                <FileTextOutlined style={{ marginRight: '8px' }} />
                {title}
              </div>
            ),
            children: (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                tabBarExtraContent={tabBarExtraContent}
                items={tabItems}
                style={styles.tabs}
              />
            )
          }
        ]}
      />
    </div>
  );
};

// Styles
const styles = {
  container: {
    marginBottom: '16px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    borderRadius: '4px'
  },
  collapseHeader: {
    fontWeight: 'bold',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center'
  },
  tabs: {
    minHeight: '400px'
  },
  editTabContent: {
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    minHeight: '350px',
    maxHeight: '600px',
    overflowY: 'auto'
  },
  documentContainer: {
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
    whiteSpace: 'pre-wrap'
  },
  templateText: {
    color: '#333'
  },
  editableFieldWrapper: {
    position: 'relative',
    display: 'inline-block'
  },
  aiIndicator: {
    position: 'absolute',
    top: '-8px',
    left: '4px',
    backgroundColor: '#6200EE',
    color: 'white',
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '1px 4px',
    borderRadius: '3px',
    fontFamily: 'Arial, sans-serif',
    zIndex: 1,
    lineHeight: '1.2'
  },
  aiIndicatorInline: {
    backgroundColor: '#6200EE',
    color: 'white',
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '1px 4px',
    borderRadius: '3px',
    fontFamily: 'Arial, sans-serif',
    marginRight: '4px',
    verticalAlign: 'middle',
    lineHeight: '1.2'
  },
  editableFieldMultiline: {
    display: 'block',
    backgroundColor: 'rgba(98, 0, 238, 0.08)',
    border: '1px solid rgba(98, 0, 238, 0.3)',
    borderRadius: '4px',
    padding: '12px',
    marginTop: '4px',
    marginBottom: '8px',
    minHeight: '60px',
    outline: 'none',
    cursor: 'text',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  },
  editableFieldInline: {
    backgroundColor: 'rgba(98, 0, 238, 0.08)',
    border: '1px solid rgba(98, 0, 238, 0.3)',
    borderRadius: '3px',
    padding: '2px 6px',
    outline: 'none',
    cursor: 'text',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    minWidth: '50px',
    display: 'inline-block'
  },
  unfilled: {
    backgroundColor: 'rgba(255, 193, 7, 0.2)',
    border: '1px dashed rgba(255, 152, 0, 0.6)',
    color: '#666',
    fontStyle: 'italic'
  },
  pdfLoadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    backgroundColor: '#fafafa',
    borderRadius: '4px'
  },
  pdfErrorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    backgroundColor: '#fff2f0',
    borderRadius: '4px',
    padding: '24px'
  },
  pdfPreviewContainer: {
    width: '100%',
    height: '600px',
    backgroundColor: '#525659',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  pdfIframe: {
    width: '100%',
    height: '100%',
    border: 'none'
  }
};

// Add focus styles via CSS-in-JS or you could add a stylesheet
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  [contenteditable]:focus {
    border-color: #6200EE !important;
    box-shadow: 0 0 0 2px rgba(98, 0, 238, 0.2) !important;
  }
  [contenteditable]:hover {
    border-color: rgba(98, 0, 238, 0.5);
  }
`;
if (typeof document !== 'undefined' && !document.querySelector('#paper-communication-styles')) {
  styleSheet.id = 'paper-communication-styles';
  document.head.appendChild(styleSheet);
}

export default PaperCommunication;